import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { soporteAPI } from "../utils/api";
import Swal from "sweetalert2";

// ─── Estilos reutilizables ─────────────────────────────────────────────────
const styles = {
  headerGradient: {
    background: "linear-gradient(87deg, #5e17eb 0%, #11cdef 100%)",
    paddingBottom: "6rem",
  },
  contactLabel: {
    fontSize: "0.7rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#8898aa",
    display: "block",
    marginBottom: "4px",
  },
  contactValue: {
    fontSize: "0.875rem",
    color: "#32325d",
    marginBottom: "2px",
  },
  contactEmail: {
    color: "#11cdef",
    textDecoration: "underline",
    fontSize: "0.875rem",
  },
  fileItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f6f9fc",
    border: "1px solid #e9ecef",
    borderRadius: "8px",
    padding: "8px 12px",
    marginBottom: "6px",
  },
  fileItemName: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "#32325d",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "220px",
  },
  fileItemSize: {
    fontSize: "0.72rem",
    color: "#8898aa",
  },
};

const MAX_FILES = 2;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ALLOWED_EXTS = ".jpg,.jpeg,.png,.webp";
const MAX_DESC_LEN = 500;

// ─── Componente principal ───────────────────────────────────────────────────
const Soporte = () => {

  // Estado del formulario
  const [categoria, setCategoria] = useState("");
  const [asunto, setAsunto] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [adjuntos, setAdjuntos] = useState([]);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  // Campos de sistema (ocultos)
  const systemInfo = useRef({});

  useEffect(() => {
    systemInfo.current = {
      browser: navigator.userAgent,
      os: navigator.platform,
      resolution: `${window.innerWidth}x${window.innerHeight}`,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };
  }, []);

  // ─── Adjuntos ──────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const incoming = Array.from(e.target.files);
    const available = MAX_FILES - adjuntos.length;
    if (available <= 0) return;

    const valid = incoming.filter((f) => ALLOWED_TYPES.includes(f.type));
    const toAdd = valid.slice(0, available);

    if (incoming.length > valid.length) {
      setErrors((prev) => ({ ...prev, adjuntos: "Solo se permiten imágenes JPG, PNG o WEBP." }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n.adjuntos; return n; });
    }

    if (toAdd.length > 0) setAdjuntos((prev) => [...prev, ...toAdd]);
    e.target.value = "";
  };

  const removeFile = (idx) => {
    setAdjuntos((prev) => prev.filter((_, i) => i !== idx));
    setErrors((prev) => { const n = { ...prev }; delete n.adjuntos; return n; });
  };

  // ─── Validación ────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!categoria) errs.categoria = "La categoría es obligatoria.";
    if (!asunto.trim()) errs.asunto = "El asunto es obligatorio.";
    else if (asunto.trim().length > 255) errs.asunto = "Máximo 255 caracteres.";
    if (!descripcion.trim()) errs.descripcion = "La descripción es obligatoria.";
    else if (descripcion.trim().length > MAX_DESC_LEN) errs.descripcion = `Máximo ${MAX_DESC_LEN} caracteres.`;
    if (adjuntos.length > MAX_FILES) errs.adjuntos = `Máximo ${MAX_FILES} archivos.`;
    return errs;
  };

  // ─── Envío ────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSending(true);

    try {
      const fd = new FormData();
      fd.append("supportCategory", categoria);
      fd.append("supportSubject", asunto.trim());
      fd.append("supportDescription", descripcion.trim());
      adjuntos.forEach((f) => fd.append("supportEvidence[]", f));
      fd.append("supportBrowser", systemInfo.current.browser || "");
      fd.append("supportOs", systemInfo.current.os || "");
      fd.append("supportResolution", systemInfo.current.resolution || "");
      fd.append("supportUrl", systemInfo.current.url || "");
      fd.append("supportTimestamp", systemInfo.current.timestamp || "");

      await soporteAPI.send(fd);

      setCategoria(""); setAsunto(""); setDescripcion(""); setAdjuntos([]); setErrors({});
      Swal.fire({
        icon: "success",
        title: "Reporte enviado",
        text: "Reporte enviado correctamente. Lo revisaremos a la brevedad.",
        confirmButtonText: "Aceptar",
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message;
      Swal.fire({
        icon: "error",
        title: "Error al enviar",
        text: msg || "No se pudo enviar el reporte. Inténtalo de nuevo.",
        confirmButtonText: "Cerrar",
      });
    } finally {
      setSending(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <>
      {/* Header degradado estilo Argon */}
      <div className="header pb-8 pt-5 pt-md-8" style={styles.headerGradient}>
        <Container fluid>
          <Row>
            <Col>
              <h1 className="text-white mb-0" style={{ fontWeight: 700, fontSize: "1.6rem" }}>
                <i className="ni ni-support-16 mr-2" />
                Ayuda
              </h1>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mt--7" fluid>
        <Row className="mt-4">
          {/* ── Card 1: ¿Necesitas ayuda? ──────────────────────────────── */}
          <Col xl="4" className="mb-4">
            <Card className="shadow h-100">
              <CardHeader className="bg-transparent border-0 pb-0">
                <Row className="align-items-center">
                  <Col>
                    <h6 className="text-uppercase mb-1" style={{ fontSize: "0.65rem", letterSpacing: "0.08em" }}>
                      ¿Necesitas ayuda?
                    </h6>
                  </Col>
                  <Col className="col-auto">
                    <div
                      className="icon icon-shape bg-gradient-info text-white rounded-circle shadow"
                      style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <i className="ni ni-headphones" style={{ fontSize: "1.1rem" }} />
                    </div>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <p className="text-muted" style={{ fontSize: "0.85rem", lineHeight: 1.6 }}>
                  Si encontraste un problema, tienes alguna duda o deseas proponer una mejora
                  para la aplicación, puedes enviarnos un reporte mediante el formulario.
                </p>

                <hr className="my-3" />

                <span style={styles.contactLabel}>Contacto de soporte</span>
                <p style={styles.contactValue} className="mb-0 font-weight-bold">Alfonso Chávez R.</p>
                <p style={styles.contactValue}>Soporte Técnico de la Aplicación</p>
                <a href="mailto:a.chavez@taqro.com.mx" style={styles.contactEmail}>
                  a.chavez@taqro.com.mx
                </a>

                <hr className="my-3" />

                <p className="text-muted mb-0" style={{ fontSize: "0.78rem" }}>
                  <i className="ni ni-time-alarm text-info mr-1" />
                  Tu reporte será revisado a la brevedad. Cuanta más información proporciones,
                  más fácil será identificar el problema.
                </p>

              </CardBody>
            </Card>
          </Col>

          {/* ── Card 2: Formulario Reporte ─────────────────────────────── */}
          <Col xl="8" className="mb-4">
            <Card className="shadow">
              <CardHeader className="bg-transparent border-0 pb-0">
                <Row className="align-items-center">
                  <Col>
                    <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: "0.65rem", letterSpacing: "0.08em" }}>
                      Formulario de contacto
                    </h6>
                    <h5 className="mb-0" style={{ fontWeight: 700, color: "#32325d" }}>
                      <i className="ni ni-email-83 mr-2 text-primary" />
                      Reporte
                    </h5>
                  </Col>
                </Row>
              </CardHeader>

              <Form onSubmit={handleSubmit} noValidate>
                <CardBody>
                  {/* Categoría + Asunto */}
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label for="supportCategory" className="form-control-label">
                          Categoría <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="select"
                          id="supportCategory"
                          name="supportCategory"
                          value={categoria}
                          onChange={(e) => { setCategoria(e.target.value); setErrors((p) => { const n = { ...p }; delete n.categoria; return n; }); }}
                          invalid={!!errors.categoria}
                          className="form-control-alternative"
                        >
                          <option value="">Seleccionar</option>
                          <option value="Error">Error</option>
                          <option value="Sugerencia">Sugerencia</option>
                          <option value="Consulta general">Consulta general</option>
                          <option value="Solicitud de mejora">Solicitud de mejora</option>
                        </Input>
                        {errors.categoria && <small className="text-danger d-block mt-1">{errors.categoria}</small>}
                      </FormGroup>
                    </Col>

                    <Col md="8">
                      <FormGroup>
                        <Label for="supportSubject" className="form-control-label">
                          Asunto <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          id="supportSubject"
                          name="supportSubject"
                          placeholder="Ej. No carga el módulo de reportes"
                          maxLength={255}
                          value={asunto}
                          onChange={(e) => { setAsunto(e.target.value); setErrors((p) => { const n = { ...p }; delete n.asunto; return n; }); }}
                          invalid={!!errors.asunto}
                          className="form-control-alternative"
                        />
                        {errors.asunto && <small className="text-danger d-block mt-1">{errors.asunto}</small>}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Descripción */}
                  <FormGroup>
                    <Label for="supportDescription" className="form-control-label">
                      Descripción <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      id="supportDescription"
                      name="supportDescription"
                      rows={6}
                      placeholder="Describe con detalle el problema o solicitud…"
                      maxLength={MAX_DESC_LEN}
                      value={descripcion}
                      onChange={(e) => { setDescripcion(e.target.value); setErrors((p) => { const n = { ...p }; delete n.descripcion; return n; }); }}
                      invalid={!!errors.descripcion}
                      className="form-control-alternative"
                    />
                    <div className="d-flex justify-content-between mt-1">
                      {errors.descripcion
                        ? <small className="text-danger">{errors.descripcion}</small>
                        : <small className="text-muted">Máximo {MAX_DESC_LEN} caracteres.</small>}
                      <small style={{ color: descripcion.length >= MAX_DESC_LEN * 0.9 ? "#f5365c" : "#8898aa" }}>
                        {descripcion.length} / {MAX_DESC_LEN}
                      </small>
                    </div>
                  </FormGroup>

                  {/* Adjuntos */}
                  <FormGroup className="mb-0">
                    <Label className="form-control-label">
                      Adjuntar evidencias{" "}
                      <span className="text-muted font-weight-normal">(opcional, máx. {MAX_FILES})</span>
                    </Label>

                    <div className="d-flex align-items-center mb-2">
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        disabled={adjuntos.length >= MAX_FILES}
                        onClick={() => fileInputRef.current?.click()}
                        className="mr-3"
                        style={{ borderRadius: "6px" }}
                        id="btnSeleccionarAdjuntos"
                      >
                        <i className="ni ni-cloud-upload-96 mr-1" />
                        Seleccionar archivos
                      </Button>
                      <small className="text-muted">
                        Formatos: JPG, PNG, WEBP · {adjuntos.length}/{MAX_FILES}
                      </small>
                    </div>

                    {/* Input oculto */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ALLOWED_EXTS}
                      multiple
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />

                    {/* Preview */}
                    {adjuntos.length === 0 ? (
                      <p className="text-muted small mb-0">No hay archivos seleccionados.</p>
                    ) : (
                      adjuntos.map((file, idx) => (
                        <div key={idx} style={styles.fileItem}>
                          <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                            <i className="ni ni-image text-info mr-2" style={{ fontSize: "1.2rem", flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                              <div style={styles.fileItemName}>{file.name}</div>
                              <div style={styles.fileItemSize}>{(file.size / 1024).toFixed(0)} KB</div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            onClick={() => removeFile(idx)}
                            style={{ borderRadius: "6px", flexShrink: 0 }}
                          >
                            <i className="ni ni-fat-remove" />
                          </Button>
                        </div>
                      ))
                    )}

                    {errors.adjuntos && <small className="text-danger d-block mt-1">{errors.adjuntos}</small>}
                  </FormGroup>
                </CardBody>

                <CardFooter className="bg-transparent d-flex justify-content-end py-3">
                  <Button
                    type="submit"
                    color="success"
                    disabled={sending}
                    style={{ borderRadius: "8px", fontWeight: 600, paddingLeft: "1.5rem", paddingRight: "1.5rem" }}
                    id="btnEnviarSoporte"
                  >
                    {sending ? (
                      <>
                        <Spinner size="sm" className="mr-2">
                          {null}
                        </Spinner>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ni ni-send mr-2" />
                        Enviar reporte
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Soporte;
